/**
 * DENSITY AND COLUMN VISIBILITY, REMEMBERED.
 *
 * ── WHY THESE TWO ARE LOCAL AND SAVED VIEWS ARE NOT ──
 *
 * A saved view is a QUESTION and it follows the person: they built it once and
 * want it on the phone they carry on the morning round. Density and column
 * visibility are ERGONOMICS — how much fits on the screen in front of you — and
 * the answer is genuinely different on a 13" laptop and a 27" desk monitor.
 * Syncing them would mean the desk machine's twelve columns arriving on the
 * laptop, which is the opposite of the preference being honoured.
 *
 * So they persist per device, in localStorage, and there is deliberately no
 * table for them.
 *
 * ── THE URL WINS, ALWAYS ──
 *
 * A stored preference is a default, not an override. If the address carries
 * `dn=compact`, that is what the reader asked for — by pasting a link, by
 * pressing Back, or by loading a saved view — and a preference from three weeks
 * ago must not quietly replace it. So the restore only fills in what the URL is
 * SILENT about, and it happens once, on mount.
 *
 * ── COLUMNS ARE PER BOOK ──
 *
 * The four books have four different column sets. One stored list would be
 * meaningless for three of them — and worse than meaningless, because the
 * resolver intersects a chosen set with the book's own columns, so a set from
 * another book produces a table missing most of its columns. Stored per book,
 * restored on the book change that needs it.
 */
import { useEffect, useRef } from 'react';
import { DashboardFilter } from '../state/filter-model';
import { FilterAction } from '../state/filter-reducer';
import { DevicePrefs, readPrefs, writePrefs } from './device-prefs-store';

export interface DevicePrefsApi {
  filter: DashboardFilter;
  dispatch: (action: FilterAction, opts?: { replace?: boolean }) => void;
  /** The query string as it was when the screen opened. Anything named in it is
   *  an explicit choice and is never overwritten by a stored preference. */
  initialSearch: string;
  /** True when a default saved view was applied. A view is a CHOICE, not
   *  silence, so a stored density must not overwrite what it asked for. */
  skip?: boolean;
}

const same = (a: string[] | null, b: string[] | null): boolean =>
  (a || []).length === (b || []).length && (a || []).every((v, i) => (b || [])[i] === v);

export const useDevicePrefs = ({
  filter,
  dispatch,
  initialSearch,
  skip = false,
}: DevicePrefsApi): void => {
  const restored = useRef(false);
  const lastBook = useRef(filter.book);

  /* ── restore, once, for whatever the URL did not say ── */
  useEffect(() => {
    if (restored.current) return;
    if (skip) return;
    restored.current = true;

    const params = new URLSearchParams(initialSearch.replace(/^\?/, ''));
    const prefs = readPrefs();
    const patch: Partial<DashboardFilter> = {};

    if (!params.has('dn') && prefs.density && prefs.density !== filter.density) {
      patch.density = prefs.density;
    }
    if (!params.has('co')) {
      const stored = prefs.columns?.[filter.book];
      if (stored && stored.length && !same(stored, filter.columns)) {
        patch.columns = stored;
      }
    }

    // REPLACE, never push. Applying a preference the reader did not choose in
    // this session must not put an entry in their history to press Back through.
    if (Object.keys(patch).length) dispatch({ type: 'patch', patch }, { replace: true });
  }, [initialSearch, filter.book, filter.density, filter.columns, dispatch, skip]);

  /* ── restore this book's columns when the book changes ── */
  useEffect(() => {
    if (!restored.current) return;
    if (filter.book === lastBook.current) return;
    const from = lastBook.current;
    lastBook.current = filter.book;

    const prefs = readPrefs();
    // Whatever was on screen belonged to the book being LEFT, so it is written
    // under that book rather than lost.
    if (filter.columns && filter.columns.length) {
      writePrefs({
        ...prefs,
        columns: { ...(prefs.columns || {}), [from]: filter.columns },
      });
    }

    const next = readPrefs().columns?.[filter.book] || null;
    if (!same(next, filter.columns)) {
      dispatch({ type: 'setColumns', columns: next }, { replace: true });
    }
  }, [filter.book, filter.columns, dispatch]);

  /* ── remember every change ── */
  useEffect(() => {
    if (!restored.current) return;
    const prefs = readPrefs();
    const next: DevicePrefs = { ...prefs, density: filter.density };

    if (filter.columns && filter.columns.length) {
      next.columns = { ...(prefs.columns || {}), [filter.book]: filter.columns };
    } else if (prefs.columns?.[filter.book]) {
      // Back to the book's default IS a choice, and it has to be recorded as
      // one — otherwise the next load restores the old set and the reset looks
      // like it did not take.
      const rest = { ...prefs.columns };
      delete rest[filter.book];
      next.columns = rest;
    }

    writePrefs(next);
  }, [filter.density, filter.columns, filter.book]);
};
